<script>
import { createEventDispatcher } from 'svelte';
import { specStore, cursorStore } from '../../stores/editorsStore.js';
import { onMount, onDestroy } from 'svelte';
import { slide } from 'svelte/transition';
import { getSectionsInDocument } from '../../services/searchService.js';
import { NOTIFICATION_TIMEOUT_MS } from '../../constants/ui.js';
import NotificationBubble from '../shared/NotificationBubble.svelte';
import DraggableResizablePanel from '../shared/DraggableResizablePanel.svelte';

const dispatch = createEventDispatcher();

let searchInputValue = '';
let searchInputElement;
let floatingPanelSearchInput;
let isSearchActive = false;
let searchResults = [];
let currentResultIndex = -1;
let windowWidth = typeof window !== 'undefined' ? window.innerWidth : 1200;
let showOptionsMenu = false;
let showNoResultsNotification = false;
let noResultsTimer = null;

// Search options
let isCaseSensitive = false;
let isReplaceMode = false;
let replaceValue = '';
let selectedSection = 'all';

// Get active editor based on cursor position (default to spec if not set)
$: activeEditor = $cursorStore.activeEditor || 'spec';

// Update placeholder text based on active editor
$: searchPlaceholder = activeEditor === 'claims' ? 'Claims find or replace' : 'Spec find or replace';

// Section types - will be populated from document
let sectionTypes = [
    { value: 'all', label: 'All Sections' }
];

// Update section types based on document content
function updateSectionTypes() {
    // Try to get sections from ProseMirror document first
    const editor = $specStore.editor;
    let actualSections = [];
    
    if (editor?.state?.doc) {
        // Use ProseMirror-aware section detection
        actualSections = getSectionsInDocument(editor.state.doc);
    }
    
    // Always start with 'All Sections'
    const updatedSections = [
        { value: 'all', label: 'All Sections' }
    ];
    
    // Add actual sections found in the document
    if (actualSections.length > 0) {
        actualSections.forEach(section => {
            updatedSections.push({
                value: section.type,
                label: section.name
            });
        });
    }
    
    sectionTypes = updatedSections;
}

// Handle search input changes
$: {
    // Hide no results notification when input changes
    showNoResultsNotification = false;
    
    // Clear any existing timer when input changes
    if (noResultsTimer) {
        clearTimeout(noResultsTimer);
        noResultsTimer = null;
    }
    
    if (searchInputValue) {
        // If we have a search term, perform search
        performSearch(searchInputValue, isCaseSensitive, selectedSection);
    } else {
        // If search input is empty, clear search results and highlighting
        searchResults = [];
        currentResultIndex = -1;
        dispatch('clearSearch');
    }
}

// Handle search result updates
function handleSearchResultsUpdated(event) {
    const { results, currentIndex } = event.detail;
    searchResults = results;
    currentResultIndex = currentIndex;
    
    // Show notification if we have a search term but no results
    if (searchInputValue && searchResults.length === 0) {
        showNoResultsMessage();
    } else {
        // Hide notification if we have results
        showNoResultsNotification = false;
    }
}

// Set up event listeners
onMount(() => {
    const handleResize = () => {
        windowWidth = window.innerWidth;
    };
    
    const handleClickOutside = (event) => {
        // Handle options menu
        if (showOptionsMenu && !event.target.closest('.options-menu') && !event.target.closest('.options-btn')) {
            showOptionsMenu = false;
        }
        
        // Handle searchbar blur when clicking outside
        const searchContainer = event.target.closest('.search-bar-container');
        const isSearchbarClick = searchContainer !== null;
        
        if (!isSearchbarClick && isSearchActive && !searchInputValue) {
            isSearchActive = false;
            dispatch('searchStateChange', { active: false });
        }
    };
    
    window.addEventListener('resize', handleResize);
    document.addEventListener('searchResultsUpdated', handleSearchResultsUpdated);
    document.addEventListener('click', handleClickOutside);
    
    // Initialize section types
    updateSectionTypes();
    
    return () => {
        window.removeEventListener('resize', handleResize);
        document.removeEventListener('searchResultsUpdated', handleSearchResultsUpdated);
        document.removeEventListener('click', handleClickOutside);
        
        // Clear any existing timer when component is destroyed
        if (noResultsTimer) {
            clearTimeout(noResultsTimer);
        }
    };
});

// Subscribe to spec editor changes to update section types
$: if ($specStore.editor) {
    updateSectionTypes();
}

// Also update when document changes
$: if ($specStore.editor?.state?.doc) {
    // The doc object changes when content is modified
    const doc = $specStore.editor.state.doc;
    updateSectionTypes();
}

// Focus floating panel search input when it appears
$: if (isReplaceMode && floatingPanelSearchInput) {
    // Use setTimeout to ensure the element is fully rendered
    setTimeout(() => {
        if (floatingPanelSearchInput) {
            floatingPanelSearchInput.focus();
            // Select all text for easy replacement
            floatingPanelSearchInput.select();
        }
    }, 50);
}

function performSearch(query, caseSensitive = false, section = 'all') {
    // Dispatch the search event with the query and search options
    dispatch('search', { 
        query,
        options: {
            caseSensitive,
            section
        },
        editor: activeEditor
    });
    
    // Keep focus on the search input after search
    if (searchInputElement) {
        setTimeout(() => {
            if (searchInputElement) {
                searchInputElement.focus();
            }
        }, 0);
    }
}

function handleKeyDown(event) {
    if (event.key === 'Enter') {
        // Prevent default behavior (form submission, line breaks in editor)
        event.preventDefault();
        
        if (isReplaceMode && event.ctrlKey) {
            // Replace all with Ctrl+Enter
            dispatch('replaceAll', { 
                search: searchInputValue, 
                replace: replaceValue,
                section: selectedSection,
                editor: activeEditor
            });
        } else if (event.shiftKey) {
            // Search previous
            dispatch('searchNavigation', { direction: 'prev', editor: activeEditor });
        } else {
            // Search next (default behavior for Enter)
            dispatch('searchNavigation', { direction: 'next', editor: activeEditor });
        }
    } else if (event.key === 'Escape') {
        // Handle Escape key press - always do a hard exit
        event.preventDefault();
        event.stopPropagation();
        isReplaceMode = false;
        selectedSection = 'all'; // Reset section filter to All Sections
        clearSearch();
        
        // Blur the search input to remove cursor
        if (searchInputElement) {
            searchInputElement.blur();
        }
        // Also blur floating panel input if it exists
        if (floatingPanelSearchInput) {
            floatingPanelSearchInput.blur();
        }
    }
}


function clearSearch() {
    searchInputValue = '';
    isSearchActive = false;
    dispatch('searchStateChange', { active: false });
    searchResults = [];
    currentResultIndex = -1;
    showNoResultsNotification = false;
    
    // Clear any existing timer
    if (noResultsTimer) {
        clearTimeout(noResultsTimer);
        noResultsTimer = null;
    }
    
    // Blur the input to ensure it's not focused
    if (searchInputElement) {
        searchInputElement.blur();
    }
    
    dispatch('clearSearch');
}

function toggleReplaceMode() {
    isReplaceMode = !isReplaceMode;
    showOptionsMenu = false;
}


function toggleOptionsMenu() {
    showOptionsMenu = !showOptionsMenu;
}

function toggleCaseSensitive() {
    isCaseSensitive = !isCaseSensitive;
    if (searchInputValue) {
        performSearch(searchInputValue, isCaseSensitive, selectedSection);
    }
}

// Handle focus for search state tracking
function handleFocus(event) {
    // Don't focus if clicking on control buttons
    if (event && event.target.closest('.search-controls')) {
        return;
    }
    
    isSearchActive = true;
    dispatch('searchStateChange', { active: true });
    // Give time for the transition
    setTimeout(() => {
        if (searchInputElement) {
            searchInputElement.focus();
        }
    }, 50);
}

// Export a function to focus the search bar programmatically
export function focusSearchBar() {
    isSearchActive = true;
    dispatch('searchStateChange', { active: true });
    // Give time for the transition
    setTimeout(() => {
        if (searchInputElement) {
            searchInputElement.focus();
            searchInputElement.select(); // Select all text when focusing via keyboard shortcut
        }
    }, 50);
}

function handleBlur(event) {
    // Only blur if clicking outside the search container
    const searchContainer = event.target.closest('.search-bar-container');
    if (!searchContainer && !searchInputValue) {
        isSearchActive = false;
        dispatch('searchStateChange', { active: false });
    }
}

function showNoResultsMessage() {
    showNoResultsNotification = true;
    
    // Clear any existing timer
    if (noResultsTimer) {
        clearTimeout(noResultsTimer);
    }
    
    // Set a new timer to hide the notification
    noResultsTimer = setTimeout(() => {
        showNoResultsNotification = false;
    }, NOTIFICATION_TIMEOUT_MS);
}
</script>

<div class="search-bar-container" class:active={isSearchActive} class:has-notification={showNoResultsNotification} class:spec-active={activeEditor === 'spec'} class:claims-active={activeEditor === 'claims'} data-testid="search-container">
    <div class="search-bar-header">
        <div class="search-icon">
            <i class="fas fa-search"></i>
        </div>
        
        <input
            type="text"
            bind:value={searchInputValue}
            bind:this={searchInputElement}
            on:focus={handleFocus}
            on:blur={handleBlur}
            on:keydown={handleKeyDown}
            placeholder={searchPlaceholder}
            data-testid="search-input"
        />
        
        <div class="search-controls" class:visible={isSearchActive || searchInputValue}>
                {#if searchResults.length > 0}
                    <span class="result-count">
                        {currentResultIndex + 1}/{searchResults.length}
                        {#if searchResults[currentResultIndex]?.section?.name}
                            <span class="section-indicator">
                                ({searchResults[currentResultIndex].section.name})
                            </span>
                        {/if}
                    </span>
                {/if}
                
                <NotificationBubble 
                    show={showNoResultsNotification}
                    message="No results found"
                    variant="error"
                    position="bottom"
                    duration={NOTIFICATION_TIMEOUT_MS}
                    on:hide={() => showNoResultsNotification = false}
                />
                
                <button 
                    class="case-btn"
                    class:active={isCaseSensitive}
                    on:click={toggleCaseSensitive}
                    title={isCaseSensitive ? "Search: Case sensitive" : "Search: Case insensitive"}
                >
                    <span class="case-icon">aA</span>
                </button>
                
                <button 
                    class="nav-btn"
                    disabled={!searchResults.length}
                    on:click={() => dispatch('searchNavigation', { direction: 'prev', editor: activeEditor })}
                    title="Previous match (Shift+Enter)"
                >
                    <i class="fas fa-chevron-up"></i>
                </button>
                
                <button 
                    class="nav-btn"
                    disabled={!searchResults.length}
                    on:click={() => dispatch('searchNavigation', { direction: 'next', editor: activeEditor })}
                    title="Next match (Enter)"
                >
                    <i class="fas fa-chevron-down"></i>
                </button>
                
                <!-- Options button with dropdown -->
                <div class="options-container">
                    <button 
                        class="options-btn"
                        on:click={toggleOptionsMenu}
                        title="Search options"
                        class:active={showOptionsMenu || isReplaceMode}
                    >
                        <i class="fas fa-cog"></i>
                    </button>
                    
                    {#if showOptionsMenu}
                        <div class="options-menu" transition:slide={{ duration: 100 }}>
                            <div class="option-item" on:click={toggleReplaceMode} on:keydown={(e) => e.key === 'Enter' && toggleReplaceMode()} role="button" tabindex="0">
                                <span class="option-icon">
                                    <i class="fas fa-exchange-alt"></i>
                                </span>
                                <span>Find and replace</span>
                            </div>
                        </div>
                    {/if}
                </div>
                
                <button class="clear-btn" on:click={clearSearch} aria-label="Clear search">
                    <i class="fas fa-times"></i>
                </button>
            </div>
    </div>

</div>

<!-- Floating Find/Replace Panel -->
<DraggableResizablePanel
    show={isReplaceMode}
    title="Find and Replace - {activeEditor === 'claims' ? 'Claims' : 'Specification'}"
    storageKey="find-replace-panel"
    theme={activeEditor === 'spec' ? 'search' : 'claims'}
    initialWidth={700}
    initialHeight={220}
    minWidth={400}
    minHeight={180}
    maxWidth={900}
    maxHeight={400}
    draggable={true}
    resizable={true}
    showCloseButton={true}
    on:close={() => {
        isReplaceMode = false;
        selectedSection = 'all';
        clearSearch();
    }}
>
    <div class="find-replace-content">
        <!-- Find row -->
        <div class="panel-row">
            <div class="row-label">
                <i class="fas fa-search"></i>
                <span>Find:</span>
            </div>
            <input 
                type="text" 
                bind:this={floatingPanelSearchInput}
                bind:value={searchInputValue}
                on:keydown={handleKeyDown}
                placeholder="Search text..."
                class="panel-input"
                data-testid="find-input"
            />
            <div class="panel-nav-buttons">
                <button 
                    class="panel-case-btn"
                    class:active={isCaseSensitive}
                    on:click={toggleCaseSensitive}
                    title={isCaseSensitive ? "Search: Case sensitive" : "Search: Case insensitive"}
                >
                    <span class="case-icon">aA</span>
                </button>
                <button 
                    class="panel-nav-btn"
                    disabled={!searchResults.length}
                    on:click={() => dispatch('searchNavigation', { direction: 'prev', editor: activeEditor })}
                    title="Previous match (Shift+Enter)"
                >
                    <i class="fas fa-chevron-up"></i>
                </button>
                <button 
                    class="panel-nav-btn"
                    disabled={!searchResults.length}
                    on:click={() => dispatch('searchNavigation', { direction: 'next', editor: activeEditor })}
                    title="Next match (Enter)"
                >
                    <i class="fas fa-chevron-down"></i>
                </button>
                <button 
                    class="panel-clear-btn"
                    on:click={clearSearch}
                    title="Clear search"
                >
                    Clear
                </button>
            </div>
            <div class="search-info">
                {#if searchResults.length > 0}
                    <span class="match-count">
                        {currentResultIndex + 1} of {searchResults.length}
                    </span>
                {:else if searchInputValue}
                    <span class="no-matches">No matches</span>
                {/if}
            </div>
        </div>
            
        <!-- Replace row -->
        <div class="panel-row">
            <div class="row-label">
                <i class="fas fa-exchange-alt"></i>
                <span>Replace:</span>
            </div>
            <input 
                type="text" 
                bind:value={replaceValue} 
                on:keydown={handleKeyDown}
                placeholder="Replace with..."
                class="panel-input"
                data-testid="replace-input"
            />
            <div class="replace-actions">
                <button 
                    class="action-btn"
                    title="Replace current match"
                    disabled={!searchResults.length}
                    on:click={() => dispatch('replace', { 
                        search: searchInputValue, 
                        replace: replaceValue,
                        section: selectedSection,
                        editor: activeEditor
                    })}
                >
                    Replace
                </button>
                <button 
                    class="action-btn primary"
                    title="Replace all matches (Ctrl+Enter)"
                    disabled={!searchResults.length}
                    on:click={() => dispatch('replaceAll', { 
                        search: searchInputValue, 
                        replace: replaceValue,
                        section: selectedSection,
                        editor: activeEditor
                    })}
                >
                    Replace All
                </button>
            </div>
        </div>
            
        <!-- Section filter row - only show for spec editor -->
        {#if activeEditor === 'spec'}
        <div class="panel-row section-row">
            <div class="row-label">
                <i class="fas fa-filter"></i>
                <span>Section:</span>
            </div>
            <select 
                bind:value={selectedSection}
                on:change={() => {
                    console.log('[SearchBar Debug] Section changed to:', selectedSection);
                    if (searchInputValue) {
                        performSearch(searchInputValue, isCaseSensitive, selectedSection);
                    }
                }}
                class="section-select"
            >
                {#each sectionTypes as sectionType}
                    <option value={sectionType.value}>{sectionType.label}</option>
                {/each}
            </select>
            <div class="section-info">
                {#if selectedSection !== 'all'}
                    <span class="active-filter">Filtering active</span>
                {/if}
            </div>
        </div>
        {/if}
    </div>
</DraggableResizablePanel>

<style>
.search-bar-container {
    position: relative;
    z-index: var(--z-index-50);
    display: flex;
    flex-direction: column;
    flex-shrink: 1; /* Allow search to shrink when space is limited */
    width: 100%;
    min-width: 100px; /* Reduced minimum width */
    max-width: 200px; /* Increased width to accommodate longer placeholder text */
    margin: 0; /* Reset margins to ensure gap works properly */
    transition: var(--transition-all);
}

/* Ensure notifications appear above floating panels */
.search-bar-container.has-notification {
    z-index: var(--z-index-notification);
}


.search-bar-container.active {
    max-width: 320px; /* Increased expanded state width */
}

.search-bar-header {
    display: flex;
    align-items: center;
    height: var(--height-toolbar-button);
    padding: 0 var(--spacing-1); /* Reduce right padding to minimize space after buttons */
    overflow: visible; /* Allow dropdown menus to show */
    background-color: var(--color-btn-default);
    border: none;
    border-radius: var(--border-radius-2xl);
    box-shadow: var(--shadow-inset-xs);
    transition: background-color 0.2s var(--ease-in-out);
    cursor: text;
    box-sizing: border-box;
}

/* When spec editor is active */
.search-bar-container.spec-active .search-bar-header {
    background-color: var(--color-btn-spec) !important;
}

.search-bar-container.spec-active.active .search-bar-header {
    background-color: var(--color-bg-spec) !important;
    box-shadow: var(--shadow-inset-md);
}

/* When claims editor is active */
.search-bar-container.claims-active .search-bar-header {
    background-color: var(--color-btn-default) !important;
}

.search-bar-container.claims-active.active .search-bar-header {
    background-color: var(--color-btn-default-hover) !important;
    box-shadow: var(--shadow-inset-md);
}

.search-icon {
    display: flex;
    justify-content: center;
    align-items: center;
    flex-shrink: 0; /* Prevent icon from shrinking */
    margin-right: var(--spacing-2);
    margin-left: var(--spacing-2);
    font-size: var(--font-size-sm);
    color: var(--color-text-secondary);
}

input {
    flex: 1;
    width: 100%;
    min-width: 50px; /* Ensure minimum width for input */
    padding: 0;
    margin: 0;
    font-size: var(--font-size-sm);
    line-height: 1.5;
    color: var(--color-text-primary);
    background: none;
    border: none;
    outline: none;
    text-overflow: ellipsis;

    /* Reset browser-specific input styling */
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;
    caret-color: var(--color-text-primary);
}

/* Spec editor input caret */
.search-bar-container.spec-active input {
    caret-color: var(--color-spec-figure-text);
}

/* Claims editor input caret */
.search-bar-container.claims-active input {
    caret-color: var(--color-text-secondary);
}

input::placeholder {
    overflow: hidden;
    font-family: inherit;
    font-size: var(--font-size-sm);
    white-space: nowrap;
    color: var(--color-text-secondary);
    opacity: 0.8;
    text-overflow: ellipsis;
    font-style: italic;
}

.search-controls {
    position: relative;
    display: flex;
    align-items: center;
    width: 0;
    overflow: hidden;
    opacity: 0;
    transition: var(--transition-all);
    gap: 2px;
}

.search-controls.visible {
    width: auto;
    margin-right: 0; /* Remove any right margin to push buttons closer to edge */
    overflow: visible;
    opacity: 1;
}

.result-count {
    display: flex;
    align-items: center;
    max-width: 180px;
    margin-right: 3px;
    overflow: hidden;
    font-size: 11px;
    font-weight: 500;
    white-space: nowrap;
    color: var(--color-text-secondary);
    text-overflow: ellipsis;
}

/* Spec editor result count colors */
.search-bar-container.spec-active .result-count {
    color: var(--color-btn-spec-hover);
}

/* Claims editor result count colors */
.search-bar-container.claims-active .result-count {
    color: var(--color-surface-header-claims);
}

.section-indicator {
    max-width: 120px;
    margin-left: var(--spacing-1);
    overflow: hidden;
    font-size: 10px;
    white-space: nowrap;
    color: var(--color-text-secondary);
    font-style: italic;
    text-overflow: ellipsis;
}

/* Notification styles moved to NotificationBubble component */

.nav-btn,
.options-btn,
.clear-btn,
.case-btn {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 22px;
    height: 22px;
    padding: 0;
    margin: 0 1px;
    font-size: 10px;
    color: var(--color-text-secondary);
    background-color: var(--color-btn-default);
    border: var(--border-width-1) solid var(--border-color-default);
    border-radius: var(--border-radius-lg);
    transition: var(--transition-button);
    cursor: pointer;
}

.nav-btn:disabled {
    opacity: var(--opacity-disabled);
    cursor: not-allowed;
}

/* Spec editor button colors */
.search-bar-container.spec-active .nav-btn,
.search-bar-container.spec-active .options-btn,
.search-bar-container.spec-active .clear-btn,
.search-bar-container.spec-active .case-btn {
    border-color: var(--color-btn-spec);
}

.search-bar-container.spec-active .nav-btn:hover:not(:disabled),
.search-bar-container.spec-active .options-btn:hover,
.search-bar-container.spec-active .clear-btn:hover,
.search-bar-container.spec-active .case-btn:hover {
    color: var(--color-white);
    background-color: var(--color-btn-spec-hover);
    border-color: var(--color-btn-spec-hover);
}

.search-bar-container.spec-active .options-btn.active,
.search-bar-container.spec-active .case-btn.active {
    color: var(--color-white);
    background-color: var(--color-btn-spec-hover);
    border-color: var(--color-btn-spec-hover);
}

/* Claims editor button colors */
.search-bar-container.claims-active .nav-btn,
.search-bar-container.claims-active .options-btn,
.search-bar-container.claims-active .clear-btn,
.search-bar-container.claims-active .case-btn {
    border-color: var(--color-surface-header-claims);
}

.search-bar-container.claims-active .nav-btn:hover:not(:disabled),
.search-bar-container.claims-active .options-btn:hover,
.search-bar-container.claims-active .clear-btn:hover,
.search-bar-container.claims-active .case-btn:hover {
    color: var(--color-white);
    background-color: var(--color-surface-header-claims);
    border-color: var(--color-surface-header-claims);
}

.search-bar-container.claims-active .options-btn.active,
.search-bar-container.claims-active .case-btn.active {
    color: var(--color-white);
    background-color: var(--color-surface-header-claims);
    border-color: var(--color-surface-header-claims);
}

.case-icon {
    font-family: monospace;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: -1px;
}

/* Options menu */
.options-container {
    position: relative;
}

.options-menu {
    position: absolute;
    top: calc(100% + 6px);
    right: 0;
    z-index: var(--z-index-dropdown);
    min-width: 160px;
    padding: var(--spacing-1);
    overflow: hidden;
    background-color: var(--color-btn-default);
    border: var(--border-width-1) solid var(--border-color-default);
    border-radius: var(--border-radius-xl);
    box-shadow: var(--shadow-dropdown);
}

/* Spec editor options menu colors */
.search-bar-container.spec-active .options-menu {
    background-color: var(--color-btn-spec);
    border-color: var(--color-btn-spec-hover);
}

/* Claims editor options menu colors */
.search-bar-container.claims-active .options-menu {
    background-color: var(--color-surface-content-claims);
    border-color: var(--color-surface-header-claims);
}

.option-item {
    display: flex;
    align-items: center;
    padding: var(--spacing-2) var(--spacing-3);
    margin: 2px;
    font-size: var(--font-size-xs);
    color: var(--color-text-secondary);
    border-radius: var(--border-radius-md);
    transition: var(--transition-button);
    cursor: pointer;
}

.option-item:hover {
    color: var(--color-white);
    background-color: var(--color-text-secondary);
}

/* Spec editor option item colors */
.search-bar-container.spec-active .option-item:hover {
    background-color: var(--color-btn-spec-hover);
}

/* Claims editor option item colors */
.search-bar-container.claims-active .option-item:hover {
    background-color: var(--color-surface-header-claims);
}

.option-checkbox {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 16px;
    height: 16px;
    margin-right: var(--spacing-2);
    background-color: var(--color-btn-default);
    border: var(--border-width-1) solid var(--color-btn-spec-hover);
    border-radius: var(--border-radius-md);
    transition: var(--transition-button);
}

.option-checkbox.checked {
    color: var(--color-white);
    background-color: var(--color-btn-spec-hover);
    border-color: var(--color-btn-spec-hover);
}

.option-checkbox.checked i {
    font-size: 8px;
}

.option-icon {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 16px;
    height: 16px;
    margin-right: var(--spacing-2);
    color: var(--color-btn-spec-hover);
    transition: var(--transition-button);
}

.option-item:hover .option-icon {
    color: var(--color-white);
}

.option-icon i {
    font-size: 10px;
}

/* Find/Replace content styling */
.find-replace-content {
    max-height: 100%;
    padding: var(--spacing-3);
    overflow-y: auto;
}

/* Scrollbar styling for find/replace panel - spec theme */
.find-replace-content::-webkit-scrollbar {
    width: 8px;
}

/* Default scrollbar styling */
.find-replace-content::-webkit-scrollbar-track {
    background: var(--color-btn-default);
    border-radius: 4px;
}

.find-replace-content::-webkit-scrollbar-thumb {
    background: var(--border-color-default);
    border: 2px solid var(--color-btn-default);
    border-radius: 4px;
}

.find-replace-content::-webkit-scrollbar-thumb:hover {
    background: var(--color-text-secondary);
}

/* Spec editor scrollbar styling */
.search-bar-container.spec-active .find-replace-content::-webkit-scrollbar-track {
    background: var(--color-surface-content-spec);
}

.search-bar-container.spec-active .find-replace-content::-webkit-scrollbar-thumb {
    background: var(--color-spec-border);
    border-color: var(--color-surface-content-spec);
}

.search-bar-container.spec-active .find-replace-content::-webkit-scrollbar-thumb:hover {
    background: var(--color-spec-figure-text);
}

/* Claims editor scrollbar uses defaults */

/* Dark mode scrollbar for find/replace panel */
:global([data-theme="dark"]) .find-replace-content::-webkit-scrollbar-track {
    background: var(--color-btn-default);
}

:global([data-theme="dark"]) .find-replace-content::-webkit-scrollbar-thumb {
    background: var(--border-color-default);
    border-color: var(--color-btn-default);
}

:global([data-theme="dark"]) .find-replace-content::-webkit-scrollbar-thumb:hover {
    background: var(--color-text-secondary);
}

/* Dark mode - Spec editor scrollbar */
:global([data-theme="dark"]) .search-bar-container.spec-active .find-replace-content::-webkit-scrollbar-track {
    background: var(--color-surface-content-spec);
}

:global([data-theme="dark"]) .search-bar-container.spec-active .find-replace-content::-webkit-scrollbar-thumb {
    background: var(--color-spec-border);
    border-color: var(--color-surface-content-spec);
}

:global([data-theme="dark"]) .search-bar-container.spec-active .find-replace-content::-webkit-scrollbar-thumb:hover {
    background: var(--color-spec-figure-text-hover);
}

/* Dark mode - Claims editor scrollbar uses defaults */

/* Override panel styling for search theme */
:global(.draggable-panel--search) :global(.draggable-panel-content) {
    padding: 0;
}

.panel-row {
    display: flex;
    align-items: center;
    margin-bottom: var(--spacing-2);
    gap: var(--spacing-2);
}

.panel-row:last-child {
    margin-bottom: 0;
}

.row-label {
    display: flex;
    align-items: center;
    width: 70px;
    font-size: var(--font-size-sm);
    color: var(--color-text-secondary);
    gap: var(--spacing-1);
}

.row-label i {
    font-size: var(--font-size-xs);
}

.panel-input {
    flex: 1;
    height: 28px;
    padding: 0 var(--spacing-2);
    font-size: var(--font-size-sm);
    color: var(--color-text-primary);
    background-color: var(--color-input-bg-floating);
    border: var(--border-width-1) solid var(--color-border);
    border-radius: var(--border-radius-lg);
    outline: none;

    /* Reset browser-specific input styling */
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;
    caret-color: var(--color-text-primary);
}

/* Spec editor panel input caret */
.search-bar-container.spec-active .panel-input {
    caret-color: var(--color-spec-figure-text);
}

/* Claims editor panel input caret */
.search-bar-container.claims-active .panel-input {
    caret-color: var(--color-text-secondary);
}

/* Input focus states - conditional based on active editor */
.search-bar-container.spec-active .panel-input:focus {
    background-color: var(--color-spec-figure-bg); /* Stronger pink on focus */
    border-color: var(--color-spec-figure-text-hover);
}

.search-bar-container.claims-active .panel-input:focus {
    background-color: var(--color-surface-content-claims);
    border-color: var(--color-surface-header-claims);
}

.search-info {
    min-width: 100px;
    margin-left: auto;
    font-size: var(--font-size-xs);
    text-align: right;
}

.match-count {
    color: var(--color-text-secondary);
}

.no-matches {
    color: var(--color-text-secondary);
    opacity: 0.8;
}

/* Spec editor no matches color */
.search-bar-container.spec-active .no-matches {
    color: var(--color-btn-spec-hover);
}

/* Claims editor no matches color */
.search-bar-container.claims-active .no-matches {
    color: var(--color-surface-header-claims);
}

.panel-nav-buttons {
    display: flex;
    gap: 4px;
}

.panel-nav-btn,
.panel-case-btn {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 24px;
    height: 24px;
    padding: 0;
    font-size: 10px;
    background-color: var(--color-input-bg-floating);
    border: var(--border-width-1) solid var(--color-border);
    border-radius: var(--border-radius-lg);
    transition: var(--transition-button);
    cursor: pointer;
}

/* Spec editor button colors */
.search-bar-container.spec-active .panel-nav-btn,
.search-bar-container.spec-active .panel-case-btn {
    color: var(--color-spec-figure-text);
}

.search-bar-container.spec-active .panel-nav-btn:hover:not(:disabled),
.search-bar-container.spec-active .panel-case-btn:hover {
    color: var(--color-white);
    background-color: var(--color-spec-figure-text);
}

/* Claims editor button colors */
.search-bar-container.claims-active .panel-nav-btn,
.search-bar-container.claims-active .panel-case-btn {
    color: var(--color-text-secondary);
}

.search-bar-container.claims-active .panel-nav-btn:hover:not(:disabled),
.search-bar-container.claims-active .panel-case-btn:hover {
    color: var(--color-white);
    background-color: var(--color-surface-header-claims);
}

.panel-nav-btn:disabled {
    opacity: var(--opacity-disabled);
    cursor: not-allowed;
}

/* Active button states */
.search-bar-container.spec-active .panel-case-btn.active {
    color: var(--color-white);
    background-color: var(--color-spec-figure-text);
}

.search-bar-container.claims-active .panel-case-btn.active {
    color: var(--color-white);
    background-color: var(--color-surface-header-claims);
}

.panel-clear-btn {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 24px;
    padding: 0 var(--spacing-2);
    margin-left: var(--spacing-1);
    font-size: var(--font-size-xs);
    font-weight: 500;
    white-space: nowrap;
    background-color: var(--color-input-bg-floating);
    border: var(--border-width-1) solid var(--color-border);
    border-radius: var(--border-radius-lg);
    transition: var(--transition-button);
    cursor: pointer;
}

/* Clear button colors */
.search-bar-container.spec-active .panel-clear-btn {
    color: var(--color-spec-figure-text);
}

.search-bar-container.spec-active .panel-clear-btn:hover {
    color: var(--color-white);
    background-color: var(--color-btn-spec-hover);
    border-color: var(--color-btn-spec-hover);
}

.search-bar-container.claims-active .panel-clear-btn {
    color: var(--color-text-secondary);
}

.search-bar-container.claims-active .panel-clear-btn:hover {
    color: var(--color-white);
    background-color: var(--color-surface-header-claims);
    border-color: var(--color-surface-header-claims);
}

.replace-actions {
    display: flex;
    gap: var(--spacing-1);
    min-width: 160px;
}

.action-btn {
    padding: 4px 12px;
    font-size: var(--font-size-sm);
    background-color: var(--color-input-bg-floating);
    border: var(--border-width-1) solid var(--color-border);
    border-radius: var(--border-radius-lg);
    transition: var(--transition-button);
    cursor: pointer;
}

/* Action button colors for spec editor */
.search-bar-container.spec-active .action-btn {
    color: var(--color-spec-figure-text);
}

.search-bar-container.spec-active .action-btn:hover:not(:disabled) {
    color: var(--color-white);
    background-color: var(--color-spec-figure-text);
}

.search-bar-container.spec-active .action-btn.primary {
    color: var(--color-white);
    background-color: var(--color-spec-figure-text);
    border-color: var(--color-spec-figure-text);
}

/* Action button colors for claims editor */
.search-bar-container.claims-active .action-btn {
    color: var(--color-text-secondary);
}

.search-bar-container.claims-active .action-btn:hover:not(:disabled) {
    color: var(--color-white);
    background-color: var(--color-surface-header-claims);
}

.search-bar-container.claims-active .action-btn.primary {
    color: var(--color-white);
    background-color: var(--color-surface-header-claims);
    border-color: var(--color-surface-header-claims);
}

/* Primary button hover states */
.search-bar-container.spec-active .action-btn.primary:hover:not(:disabled) {
    background-color: var(--color-spec-figure-text-hover);
    border-color: var(--color-spec-figure-text-hover);
}

.search-bar-container.claims-active .action-btn.primary:hover:not(:disabled) {
    background-color: var(--color-surface-content-claims);
    border-color: var(--color-surface-content-claims);
}

.action-btn:disabled {
    opacity: var(--opacity-disabled);
    cursor: not-allowed;
}

.section-select {
    flex: 1;
    height: 28px;
    padding: 0 var(--spacing-2);
    font-size: var(--font-size-sm);
    color: var(--color-text-primary);
    background-color: var(--color-input-bg-floating);
    border: var(--border-width-1) solid var(--color-border);
    border-radius: var(--border-radius-lg);
    outline: none;
    cursor: pointer;
}

/* Section select focus states */
.search-bar-container.spec-active .section-select:focus {
    background-color: var(--color-spec-figure-bg); /* Stronger pink on focus */
    border-color: var(--color-spec-figure-text-hover);
}

.search-bar-container.claims-active .section-select:focus {
    background-color: var(--color-surface-content-claims);
    border-color: var(--color-surface-header-claims);
}

/* Section option backgrounds */
.search-bar-container.spec-active .section-select option {
    background-color: var(--color-surface-content-spec);
}

.search-bar-container.claims-active .section-select option {
    background-color: var(--color-surface-content-claims);
}

.section-info {
    min-width: 100px;
    font-size: var(--font-size-xs);
    text-align: right;
}

.active-filter {
    font-weight: 500;
    color: var(--color-text-secondary);
}

/* Spec editor active filter color */
.search-bar-container.spec-active .active-filter {
    color: var(--color-btn-spec-hover);
}

/* Claims editor active filter color */
.search-bar-container.claims-active .active-filter {
    color: var(--color-surface-header-claims);
}

.section-row {
    padding-top: var(--spacing-2);
    border-top: 1px solid var(--border-color-default);
}

/* Spec editor section row border */
.search-bar-container.spec-active .section-row {
    border-top-color: var(--color-spec-figure-text);
}

/* Claims editor section row border */
.search-bar-container.claims-active .section-row {
    border-top-color: var(--color-surface-header-claims);
}


/* Dark mode styles */
:global([data-theme="dark"]) .search-bar-header {
    background-color: var(--color-btn-default);
    box-shadow: var(--shadow-inset-xs);
}

/* Dark mode - Spec editor search header */
:global([data-theme="dark"]) .search-bar-container.spec-active .search-bar-header {
    background-color: var(--color-btn-spec) !important;
}

/* Dark mode - Claims editor search header */
:global([data-theme="dark"]) .search-bar-container.claims-active .search-bar-header {
    background-color: var(--color-btn-default) !important;
}

:global([data-theme="dark"]) .search-bar-container.active .search-bar-header {
    background-color: var(--color-btn-default-hover);
    box-shadow: var(--shadow-inset-sm);
}

/* Dark mode - Spec editor active search header */
:global([data-theme="dark"]) .search-bar-container.spec-active.active .search-bar-header {
    background-color: var(--color-btn-spec-hover) !important;
}

/* Dark mode - Claims editor active search header */
:global([data-theme="dark"]) .search-bar-container.claims-active.active .search-bar-header {
    background-color: var(--color-btn-default-hover) !important;
}

:global([data-theme="dark"]) .nav-btn,
:global([data-theme="dark"]) .options-btn,
:global([data-theme="dark"]) .clear-btn,
:global([data-theme="dark"]) .case-btn {
    color: var(--color-text-secondary);
    background-color: var(--color-gray-300);
    border-color: var(--border-color-default);
}

/* Dark mode - Spec editor button colors */
:global([data-theme="dark"]) .search-bar-container.spec-active .nav-btn,
:global([data-theme="dark"]) .search-bar-container.spec-active .options-btn,
:global([data-theme="dark"]) .search-bar-container.spec-active .clear-btn,
:global([data-theme="dark"]) .search-bar-container.spec-active .case-btn {
    border-color: var(--color-btn-spec);
}

:global([data-theme="dark"]) .search-bar-container.spec-active .nav-btn:hover:not(:disabled),
:global([data-theme="dark"]) .search-bar-container.spec-active .options-btn:hover,
:global([data-theme="dark"]) .search-bar-container.spec-active .clear-btn:hover,
:global([data-theme="dark"]) .search-bar-container.spec-active .case-btn:hover {
    color: var(--color-text-primary);
    background-color: var(--color-btn-spec-hover);
    border-color: var(--color-btn-spec-hover);
}

:global([data-theme="dark"]) .search-bar-container.spec-active .options-btn.active,
:global([data-theme="dark"]) .search-bar-container.spec-active .case-btn.active {
    color: var(--color-text-primary);
    background-color: var(--color-btn-spec-hover);
    border-color: var(--color-btn-spec-hover);
}

/* Dark mode - Claims editor button colors */
:global([data-theme="dark"]) .search-bar-container.claims-active .nav-btn,
:global([data-theme="dark"]) .search-bar-container.claims-active .options-btn,
:global([data-theme="dark"]) .search-bar-container.claims-active .clear-btn,
:global([data-theme="dark"]) .search-bar-container.claims-active .case-btn {
    border-color: var(--color-surface-header-claims);
}

:global([data-theme="dark"]) .search-bar-container.claims-active .nav-btn:hover:not(:disabled),
:global([data-theme="dark"]) .search-bar-container.claims-active .options-btn:hover,
:global([data-theme="dark"]) .search-bar-container.claims-active .clear-btn:hover,
:global([data-theme="dark"]) .search-bar-container.claims-active .case-btn:hover {
    color: var(--color-text-primary);
    background-color: var(--color-surface-header-claims);
    border-color: var(--color-surface-header-claims);
}

:global([data-theme="dark"]) .search-bar-container.claims-active .options-btn.active,
:global([data-theme="dark"]) .search-bar-container.claims-active .case-btn.active {
    color: var(--color-text-primary);
    background-color: var(--color-surface-header-claims);
    border-color: var(--color-surface-header-claims);
}

:global([data-theme="dark"]) .options-menu {
    background-color: var(--color-btn-default);
    border-color: var(--border-color-default);
}

/* Dark mode - Spec editor options menu */
:global([data-theme="dark"]) .search-bar-container.spec-active .options-menu {
    background-color: var(--color-btn-spec);
    border-color: var(--color-btn-spec-hover);
}

/* Dark mode - Claims editor options menu */
:global([data-theme="dark"]) .search-bar-container.claims-active .options-menu {
    background-color: var(--color-surface-content-claims);
    border-color: var(--color-surface-header-claims);
}

:global([data-theme="dark"]) .option-item {
    color: var(--color-text-primary);
}

:global([data-theme="dark"]) .option-item:hover {
    color: var(--color-text-primary);
    background-color: var(--color-text-secondary);
}

/* Dark mode - Spec editor option item hover */
:global([data-theme="dark"]) .search-bar-container.spec-active .option-item:hover {
    background-color: var(--color-btn-spec-hover);
}

/* Dark mode - Claims editor option item hover */
:global([data-theme="dark"]) .search-bar-container.claims-active .option-item:hover {
    background-color: var(--color-surface-header-claims);
}

:global([data-theme="dark"]) .option-checkbox {
    background-color: var(--color-checkbox-bg-dark);
    border-color: var(--color-btn-spec-hover);
}

:global([data-theme="dark"]) .option-checkbox.checked {
    color: var(--color-text-primary);
    background-color: var(--color-btn-spec-hover);
    border-color: var(--color-btn-spec-hover);
}


:global([data-theme="dark"]) .panel-input {
    color: var(--color-text-primary);
    background-color: var(--color-input-bg-floating);
    border-color: var(--border-color-default);
}

:global([data-theme="dark"]) .panel-input:focus {
    background-color: var(--color-input-bg-floating);
    border-color: var(--border-color-default);
}

/* Dark mode - Spec editor panel input */
:global([data-theme="dark"]) .search-bar-container.spec-active .panel-input {
    background-color: var(--color-input-bg-spec-dark);
    border-color: var(--color-spec-figure-text);
}

:global([data-theme="dark"]) .search-bar-container.spec-active .panel-input:focus {
    background-color: var(--color-input-bg-spec-dark-focus);
    border-color: var(--color-spec-figure-text);
}

/* Dark mode - Claims editor panel input */
:global([data-theme="dark"]) .search-bar-container.claims-active .panel-input {
    background-color: var(--color-surface-content-claims);
    border-color: var(--color-surface-header-claims);
}

:global([data-theme="dark"]) .search-bar-container.claims-active .panel-input:focus {
    background-color: var(--color-surface-content-claims);
    border-color: var(--color-surface-header-claims);
}

:global([data-theme="dark"]) .action-btn {
    color: var(--color-text-secondary);
    background-color: var(--color-btn-default);
    border-color: var(--border-color-default);
}

:global([data-theme="dark"]) .action-btn:hover:not(:disabled) {
    color: var(--color-text-primary);
    background-color: var(--color-btn-default-hover);
}

:global([data-theme="dark"]) .action-btn.primary {
    color: var(--color-white);
    background-color: var(--color-text-secondary);
    border-color: var(--color-text-secondary);
}

:global([data-theme="dark"]) .action-btn.primary:hover:not(:disabled) {
    background-color: var(--color-text-primary);
    border-color: var(--color-text-primary);
}

/* Dark mode - Spec editor action buttons */
:global([data-theme="dark"]) .search-bar-container.spec-active .action-btn {
    color: var(--color-spec-figure-text);
    background-color: var(--color-action-btn-bg-spec-dark);
    border-color: var(--color-spec-figure-text);
}

:global([data-theme="dark"]) .search-bar-container.spec-active .action-btn:hover:not(:disabled) {
    background-color: var(--color-spec-figure-text);
}

:global([data-theme="dark"]) .search-bar-container.spec-active .action-btn.primary {
    background-color: var(--color-spec-figure-text);
    border-color: var(--color-spec-figure-text);
}

:global([data-theme="dark"]) .search-bar-container.spec-active .action-btn.primary:hover:not(:disabled) {
    background-color: var(--color-spec-figure-text-hover);
    border-color: var(--color-spec-figure-text-hover);
}

/* Dark mode - Claims editor action buttons use defaults */

:global([data-theme="dark"]) .panel-nav-btn,
:global([data-theme="dark"]) .panel-case-btn {
    color: var(--color-text-secondary);
    background-color: var(--color-btn-default);
    border-color: var(--border-color-default);
}

:global([data-theme="dark"]) .panel-nav-btn:hover:not(:disabled),
:global([data-theme="dark"]) .panel-case-btn:hover {
    color: var(--color-text-primary);
    background-color: var(--color-btn-default-hover);
}

:global([data-theme="dark"]) .panel-case-btn.active {
    color: var(--color-text-primary);
    background-color: var(--color-btn-default-hover);
}

:global([data-theme="dark"]) .panel-clear-btn {
    color: var(--color-text-secondary);
    background-color: var(--color-btn-default);
    border-color: var(--border-color-default);
}

:global([data-theme="dark"]) .panel-clear-btn:hover {
    color: var(--color-text-primary);
    background-color: var(--color-btn-default-hover);
    border-color: var(--color-btn-default-hover);
}

/* Dark mode - Spec editor panel buttons */
:global([data-theme="dark"]) .search-bar-container.spec-active .panel-nav-btn,
:global([data-theme="dark"]) .search-bar-container.spec-active .panel-case-btn {
    color: var(--color-spec-figure-text);
    background-color: var(--color-action-btn-bg-spec-dark);
    border-color: var(--color-spec-figure-text);
}

:global([data-theme="dark"]) .search-bar-container.spec-active .panel-nav-btn:hover:not(:disabled),
:global([data-theme="dark"]) .search-bar-container.spec-active .panel-case-btn:hover {
    background-color: var(--color-spec-figure-text);
}

:global([data-theme="dark"]) .search-bar-container.spec-active .panel-case-btn.active {
    background-color: var(--color-spec-figure-text);
}

:global([data-theme="dark"]) .search-bar-container.spec-active .panel-clear-btn {
    color: var(--color-spec-figure-text);
    background-color: var(--color-action-btn-bg-spec-dark);
    border-color: var(--color-spec-figure-text);
}

:global([data-theme="dark"]) .search-bar-container.spec-active .panel-clear-btn:hover {
    background-color: var(--color-btn-spec-hover);
    border-color: var(--color-btn-spec-hover);
}

/* Dark mode - Claims editor panel buttons use defaults */

:global([data-theme="dark"]) .section-select {
    color: var(--color-text-primary);
    background-color: var(--color-input-bg-floating);
    border-color: var(--border-color-default);
}

:global([data-theme="dark"]) .section-select:focus {
    background-color: var(--color-input-bg-floating);
    border-color: var(--border-color-default);
}

:global([data-theme="dark"]) .section-select option {
    color: var(--color-text-primary);
    background-color: var(--color-btn-default);
}

:global([data-theme="dark"]) .section-select option:hover,
:global([data-theme="dark"]) .section-select option:checked {
    background-color: var(--color-btn-default-hover);
}

/* Dark mode - Spec editor section select */
:global([data-theme="dark"]) .search-bar-container.spec-active .section-select {
    background-color: var(--color-input-bg-spec-dark);
    border-color: var(--color-spec-figure-text);
}

:global([data-theme="dark"]) .search-bar-container.spec-active .section-select:focus {
    background-color: var(--color-input-bg-spec-dark-focus);
    border-color: var(--color-spec-figure-text);
}

:global([data-theme="dark"]) .search-bar-container.spec-active .section-select option {
    background-color: var(--color-surface-content-spec);
}

:global([data-theme="dark"]) .search-bar-container.spec-active .section-select option:hover,
:global([data-theme="dark"]) .search-bar-container.spec-active .section-select option:checked {
    background-color: var(--color-btn-spec-hover);
}

/* Dark mode - Claims editor section select uses defaults */

:global([data-theme="dark"]) .result-count {
    color: var(--color-text-secondary);
}

:global([data-theme="dark"]) .section-row {
    border-top-color: var(--border-color-default);
}

/* Dark mode - Spec editor section row border */
:global([data-theme="dark"]) .search-bar-container.spec-active .section-row {
    border-top-color: var(--color-spec-figure-text);
}

/* Dark mode - Claims editor section row border */
:global([data-theme="dark"]) .search-bar-container.claims-active .section-row {
    border-top-color: var(--color-surface-header-claims);
}

@media (width <= 1600px) and (width >= 1366px) {
    /* At minimum viewport, use slightly smaller width */
    .search-bar-container.active {
        max-width: 260px; /* Balanced for minimum viewport */
    }
    
}

@media (width <= 1200px) {
    .search-bar-header {
        padding: 0 var(--spacing-3);
    }
    
    input {
        padding: 0;
        font-size: 13px;
    }
    
    input::placeholder {
        font-size: 13px;
    }
}

@media (width <= 1024px) {
    .search-bar-header {
        padding: 0 var(--spacing-2);
    }
    
    input {
        padding: 0;
        font-size: var(--font-size-xs);
    }
    
    input::placeholder {
        font-size: var(--font-size-xs);
    }
}

@media (width <= 900px) {
    .search-bar-container {
        min-width: 100px;
        max-width: 140px;
    }
    
    .search-bar-container.active {
        max-width: 240px; /* Slightly reduced for medium viewports */
    }
    
    .search-bar-header {
        height: var(--height-toolbar-button-md);
        padding: 0 var(--spacing-2);
    }
    
    /* Hide section indicator on small screens */
    .section-indicator {
        display: none;
    }
    
    
    .row-label {
        width: 60px;
        font-size: var(--font-size-xs);
    }
}

@media (width <= 768px) {
    .search-bar-container {
        min-width: 80px;
        max-width: 110px;
    }
    
    .search-bar-container.active {
        max-width: 200px; /* Slightly increased for better dropdown fit */
    }
    
    .search-bar-header {
        height: var(--height-toolbar-button-sm);
        padding: 0 var(--spacing-2);
    }
    
    input {
        font-size: var(--font-size-xs);
    }
    
    input::placeholder {
        font-size: var(--font-size-xs);
    }
    
    .search-icon {
        margin-right: var(--spacing-1);
        font-size: var(--font-size-xs);
    }
    
    .nav-btn, .options-btn, .clear-btn {
        width: 20px;
        height: 20px;
        font-size: 9px;
    }
    
    /* Hide result count on very small screens */
    .result-count {
        display: none;
    }
    
    
    .panel-row {
        flex-wrap: wrap;
    }
    
    .row-label {
        width: 100%;
        margin-bottom: var(--spacing-1);
    }
    
    .replace-actions {
        width: 100%;
        margin-top: var(--spacing-1);
    }
    
    .action-btn {
        flex: 1;
    }
}
</style>